import { css } from "@emotion/css";
import { Checkbox, EmptyStateNoResults, List, ListItemContent, ListItemLink, Pagination, type PrimaryPageAction, SearchInput, InlineEmptyStateOnboarding } from "@octopusdeploy/design-system-components";
import { space } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { CertificateResource, EnvironmentResource, Repository, ResourceCollection, TagSetResource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { links, type QueryParamValuesSetter } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { createAddCertificateAnalyticsEvent } from "~/areas/library/components/Certificates/Certificate/amplitudeAnalytics";
import { BorderedContent } from "~/components/BorderedContent/BorderedContent";
import { PortalEmptyStateOnboarding } from "~/components/GettingStarted/PortalEmptyStateOnboarding";
import { formatUrl } from "~/components/Navigation/ExternalLink/ExternalLink";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import { PageContent } from "~/components/PageContent/PageContent";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import CertificateSummary from "~/components/form/CertificateSearch/CertificateSummary";
import { useDebouncedExternalState } from "~/hooks/useDebouncedExternalState";
interface CertificatesPageProps {
    spaceId: string;
    queryParams: CertificatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<CertificatesPageQueryParams>;
    loaderData: CertificatesPageLoaderData;
}
export interface CertificatesPageQueryParams {
    page: number | undefined;
    pageSize: number | undefined;
    search: string;
    archived: boolean | undefined;
}
export interface CertificatesPageLoaderData {
    certificatesListData: CertificatesListData;
    tenantTagSets: TagSetResource[];
    certificateStats: {
        hasAnyCertificate: boolean;
        archivedCertificateCount: number;
    };
}
interface CertificatesListData {
    certificates: ResourceCollection<CertificateResource>;
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    requestParams: {
        search: string;
        archived: boolean;
    };
}
export const certificatesPageTitle = "Certificates";
const defaultPageSize = 30;
export async function certificatesPageLoader(repository: Repository, queryParams: CertificatesPageQueryParams): Promise<CertificatesPageLoaderData> {
    const certificatesPromise = getPaginatedCertificates(repository, queryParams.page, queryParams.pageSize, queryParams.search, queryParams.archived);
    const tenantTagSetsPromise = repository.TagSets.all();
    const archivedCertificatePromise = repository.Certificates.list({
        take: 0,
        archived: true,
    });
    const liveCertificatesPromise = repository.Certificates.list({
        take: 0,
        archived: false,
    });
    return {
        certificatesListData: await certificatesPromise,
        tenantTagSets: await tenantTagSetsPromise,
        certificateStats: { hasAnyCertificate: (await archivedCertificatePromise).TotalResults + (await liveCertificatesPromise).TotalResults > 0, archivedCertificateCount: (await archivedCertificatePromise).TotalResults },
    };
}
export default function CertificatesPage({ spaceId, queryParams, setQueryParams, loaderData }: CertificatesPageProps) {
    const trackEvent = useTrackEvent();
    const certificatesQuery = usePagedCertificatesQuery(queryParams, loaderData.certificatesListData);
    const addCertificateClicked = () => trackEvent(createAddCertificateAnalyticsEvent("Page"));
    const addCertificatePageAction: PrimaryPageAction = {
        type: "navigate",
        hasPermissions: isAllowed({ permission: Permission.CertificateCreate, wildcard: true }),
        label: "Add Certificate",
        path: links.createCertificatePage.generateUrl({ spaceId }),
        onClick: addCertificateClicked,
    };
    const onPageChange = React.useCallback((page: number) => setQueryParams({ ...queryParams, page }), [queryParams, setQueryParams]);
    const onPageSizeChange = React.useCallback((pageSize: number) => setQueryParams({ ...queryParams, pageSize, page: 1 }), [queryParams, setQueryParams]);
    if (!loaderData.certificateStats.hasAnyCertificate && !queryParams.search) {
        return (<PortalEmptyStateOnboarding title={"Add certificates to use in your projects"} description={"Securely store and manage certificates in Octopus and add a layer of security for your projects and infrastructure."} primaryAction={addCertificatePageAction} learnMore={<ExternalLink href="CertificatesDocumentation">Learn more</ExternalLink>}/>);
    }
    const { certificates: certificatesResponse, environments, tenants, requestParams } = certificatesQuery.result;
    const hideBottomPagination = certificatesResponse.Items.length < 20;
    return (<PageContent header={{ title: certificatesPageTitle, primaryAction: addCertificatePageAction }} pagination={{
            ui: (<Pagination label="Certificates" totalResults={certificatesResponse.TotalResults} itemsPerPageOptions={[defaultPageSize, 50, 100]} selectedItemsPerPage={certificatesResponse.ItemsPerPage} currentPage={queryParams.page ?? 1} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange}/>),
            placement: hideBottomPagination ? "top" : "topAndBottom",
        }} filters={{
            inputs: [<FilterSection queryParams={queryParams} setQueryParams={setQueryParams} archivedCertificateCount={loaderData.certificateStats.archivedCertificateCount}/>],
        }}>
            <BorderedContent>
                {certificatesResponse.TotalResults === 0 && !requestParams.search ? (requestParams.archived ? (<EmptyStateNoResults />) : (<InlineEmptyStateOnboarding entityName={"available certificates"} primaryAction={addCertificatePageAction} learnMore={{
                label: "Docs: Certificates",
                path: formatUrl("CertificatesDocumentation"),
            }}/>)) : (<List items={certificatesResponse.Items} renderRow={({ item }) => <CertificateListItem certificate={item} tenants={tenants} environments={environments} tenantTags={loaderData.tenantTagSets}/>} rowKey={(item) => item.Id} empty={<EmptyStateNoResults />}/>)}
            </BorderedContent>
        </PageContent>);
}
async function getPaginatedCertificates(repository: Repository, page: number = 1, pageSize: number = defaultPageSize, searchFilter: string = "", archivedFilter = false) {
    const filterBasedParams = {
        search: searchFilter,
        archived: archivedFilter,
    };
    const requestParams = {
        skip: (page - 1) * pageSize,
        take: pageSize,
        ...filterBasedParams,
    };
    const certificatesResponse = await repository.Certificates.list(requestParams);
    const environmentIds = [...new Set(certificatesResponse.Items.flatMap((x) => x.EnvironmentIds))];
    const tenantIds = [...new Set(certificatesResponse.Items.flatMap((x) => x.TenantIds))];
    const environmentsPromise = repository.Environments.all({ ids: environmentIds });
    const tenants = await (isAllowed({ permission: Permission.TenantView, tenant: "*" }) ? repository.Tenants.all({ ids: tenantIds }) : []);
    return { certificates: certificatesResponse, environments: await environmentsPromise, tenants, requestParams: filterBasedParams };
}
function usePagedCertificatesQuery(queryParams: CertificatesPageQueryParams, initialResult: CertificatesListData) {
    return useQuery((repository) => getPaginatedCertificates(repository, queryParams.page, queryParams.pageSize, queryParams.search, queryParams.archived), [queryParams.page, queryParams.pageSize, queryParams.search, queryParams.archived], "Get paginated certificates and related data", { initialResult });
}
function FilterSection({ queryParams, setQueryParams, archivedCertificateCount }: {
    queryParams: CertificatesPageQueryParams;
    setQueryParams: QueryParamValuesSetter<CertificatesPageQueryParams>;
    archivedCertificateCount: number;
}) {
    const [debouncedSearchParam, setDebouncedSearchParam] = useDebouncedExternalState(queryParams.search, (value) => setQueryParams((prev) => ({ ...prev, search: value, page: 1 })), 200);
    return (<section className={filterSectionStyles}>
            <SearchInput value={debouncedSearchParam} onChange={setDebouncedSearchParam} accessibleName={"Search by name, subject or thumb"} placeholder={"Search by name, subject or thumb"}/>
            <span className={archivedCheckboxWrapperStyles}>
                <Checkbox value={queryParams.archived ?? false} onChange={() => setQueryParams((prev) => ({ ...prev, archived: !queryParams.archived, page: 1 }))} label={`Only show archived (${archivedCertificateCount})`} noMargin={true} accessibleName={"Only show archived certificates"} disabled={archivedCertificateCount === 0 && !queryParams.archived}/>
            </span>
        </section>);
}
function CertificateListItem({ certificate, environments, tenants, tenantTags }: {
    certificate: CertificateResource;
    environments: EnvironmentResource[];
    tenants: TenantResource[];
    tenantTags: TagSetResource[];
}) {
    return (<ListItemLink href={links.editCertificatePage.generateUrl({ spaceId: certificate.SpaceId, certificateId: certificate.Id })}>
            <ListItemContent>
                <CertificateSummary certificate={certificate} environments={environments} tenants={tenants} tenantTags={tenantTags}/>
            </ListItemContent>
        </ListItemLink>);
}
const filterSectionStyles = css({
    display: "flex",
    gap: space[24],
});
const archivedCheckboxWrapperStyles = css({
    alignSelf: "center",
});
